import { api } from './client';

export type SkillProgress = {
  skill: string;
  level: string;
  next_level: string;
  score: number;
};

export type WeakType = {
  block_type: string;
  skill: string;
  attempts: number;
  accuracy: number;
  weakness: number;
};

/**
 * Можно ли показывать ученику его уровень.
 *
 * ⚠️ ПРАВИЛО ЖИВЁТ ЗДЕСЬ, А НЕ В УСЛОВИИ ВНУТРИ ВЁРСТКИ. Уровень показывают в
 * трёх местах — заметка на телефоне, строка трекера и значок рядом с ним, — и
 * пока правило было размазано по ним, достаточно было забыть его в одном, чтобы
 * ученик снова увидел выдуманный «A1». Отдельная функция ещё и проверяема
 * напрямую: условие в вёрстке сторожем не поймать.
 */
export function levelIsKnown(p?: CEFRProgress | null): p is CEFRProgress {
  return Boolean(p?.measured && p.overall_level);
}

export type CEFRProgress = {
  /**
   * Уровень ИЗМЕРЕН, а не подставлен по умолчанию.
   *
   * ⚠️ Пока измерений нет, сервер отдаёт «A1» — это начальное значение, а не
   * вывод об ученике. Показывать его как уровень нельзя: 07.09.2026 уровень
   * был измерен у 9 учеников из 1167, и остальные читали про себя выдумку.
   */
  measured: boolean;
  overall_level: string;
  next_level: string;
  total_points: number;
  points_to_next: number;
  skills: SkillProgress[];
  weak: WeakType[];
};

export type DrillItem = {
  item_id: string;
  exercise_type: string;
  skill: string;
  content: Record<string, unknown>;
};

export type DrillResult = {
  is_correct: boolean;
  points_awarded: number;
  skill: string;
  feedback?: string; // essay only
  score?: number; // essay: 0-9 band
};

export const trainersApi = {
  async getCefrProgress() {
    const { data } = await api.get('/trainers/cefr-progress');
    return data as CEFRProgress;
  },
  async getWeakness() {
    const { data } = await api.get('/trainers/weakness');
    return data as { weak: WeakType[]; all: WeakType[] };
  },
  async boost(program: string) {
    const { data } = await api.post('/trainers/boost', null, { params: { program } });
    return (data as { items: DrillItem[] }).items ?? [];
  },
  async drillAnswer(itemId: string, answerData: string) {
    const { data } = await api.post(`/trainers/drill/${itemId}/answer`, { answer_data: answerData });
    return data as DrillResult;
  },
};

/**
 * Разбор по навыкам и типам заданий — для виджета «сильные и слабые стороны».
 *
 * Приходят ТОЛЬКО навыки, открытые тарифом: решает сервер, а не экран. Иначе
 * закрытый навык был бы виден всем, у кого экран открыт напрямую по адресу.
 */
export type BreakdownType = {
  type: string;
  total: number;
  correct: number;
  state: 'strong' | 'weak' | 'unknown';
};

export type BreakdownSkill = {
  skill: string;
  total: number;
  correct: number;
  types: BreakdownType[];
};

export const breakdownApi = {
  async get() {
    const { data } = await api.get('/ielts/trainer/breakdown');
    return (data as { skills: BreakdownSkill[] }).skills ?? [];
  },
};
